import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Table } from "antd";
import { RightOutlined, DownOutlined } from "@ant-design/icons";
import { MinusSquare, PlusSquare } from "feather-icons-react/build/IconComponents";

const DataTable = ({
  columns,
  dataSource,
  defaultPageSize = 10,
  expandable,
  searchable = true,
  selectable = false,
  filtersConfig = [],
  initialSort = { key: '_id', direction: 'desc' } 
}) => { 
  // State management
  const [searchText, setSearchText] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});
  const [sortConfig, setSortConfig] = useState(initialSort);

  // Memoize filtersConfig to prevent unnecessary changes
  const stableFiltersConfig = useMemo(() => filtersConfig, [filtersConfig]);

  // Initialize filters only once
 useEffect(() => {
  if (filtersConfig.length > 0 && !Object.keys(activeFilters).length) {
    const initialFilters = {};
    let needsUpdate = false;
    
    filtersConfig.forEach(filter => {
      initialFilters[filter.key] = null;
      if (activeFilters[filter.key] !== null) {
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setActiveFilters(initialFilters);
    }
  }
}, [filtersConfig.length, activeFilters]);

const flattenObject = (obj, prefix = '') => {
  return Object.keys(obj).reduce((acc, key) => {
    const prefixedKey = prefix ? `${prefix}.${key}` : key;
    if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
      Object.assign(acc, flattenObject(obj[key], prefixedKey));
    } else {
      acc[prefixedKey] = obj[key];
    }
    return acc;
  }, {});
};
  // Memoize the filtered data calculation
  const filteredData = useMemo(() => {  
    let result = dataSource || [];
    
    // Apply search filter if enabled
    // if (searchable && searchText) {
    //   result = result.filter(record =>
    //     Object.values(record).some(
    //       field => String(field).toLowerCase().includes(searchText.toLowerCase())
    //   ))};
      if (searchable && searchText) {
        const lowerSearchText = searchText.toLowerCase();
        result = result.filter(record => {
          // Create a flattened version of the record for searching
          const flatRecord = flattenObject(record);
          
          // Search through all stringifiable values in the flattened record
          return Object.values(flatRecord).some(value => {
            if (value === null || value === undefined) return false;
            return String(value).toLowerCase().includes(lowerSearchText);
          });
        });
      }
    // Apply configured filters
    stableFiltersConfig.forEach(({ key, filterFn }) => {
      if (activeFilters[key]) {
        result = filterFn 
          ? filterFn(result, activeFilters[key])
          : result.filter(record => record[key] === activeFilters[key]);
      }
    });

    // Apply sorting
    if (sortConfig) {
      const { key, direction, sortFn } = sortConfig;
      result = sortFn
        ? sortFn(result, direction)
        : [...result].sort((a, b) => {
            if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
            if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
            return 0;
          });
    }

    return result;
  }, [dataSource, searchText, activeFilters, sortConfig, stableFiltersConfig, searchable]);

  // Memoize handler functions
  const handleFilterChange = useCallback((filterKey, value) => {
    setActiveFilters(prev => ({
      ...prev,
      [filterKey]: value === prev[filterKey] ? null : value
    }));
  }, []);



  // Memoize default expandable configuration
  // DataTable.js
const defaultExpandable = useMemo(() => ({
  expandedRowRender: (record) => (
    <div className="expanded-row-container">
      <table className="expanded-row-table">
        <tbody>
          {Object.entries(record).map(([key, value]) => (
            <tr key={key}>
              <td className="field-label">
                {key.replace(/([A-Z])/g, ' $1').trim()}:
              </td>
              <td className="field-value">
                {value !== null && value !== undefined ? value.toString() : '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ),
  expandIcon: ({ expanded, onExpand, record }) =>
    expanded ? (
      <MinusSquare 
        onClick={(e) => onExpand(record, e)}
        className="expand-icon"
        size={16}
      />
    ) : (
      <PlusSquare 
        onClick={(e) => onExpand(record, e)}
        className="expand-icon"
        size={16}
      />
    ),
  rowExpandable: () => true,
}), []);

  // Memoize filter controls rendering
  const filterControls = useMemo(() => {
    if (!(stableFiltersConfig.length > 0 || searchable)) return null;
    
    return (
      <div className="d-flex mb-3 flex-wrap align-items-center">
        {stableFiltersConfig.map((filter) => (
          <FilterDropdown
            key={filter?.key}
            title={filter?.title}
            options={filter?.options}
            activeValue={activeFilters[filter.key]}
            onChange={(value) => handleFilterChange(filter.key, value)}
            customRender={filter.customRender}
          />
        ))}

        {searchable && (
          <input
            type="search"
            onChange={(e) => setSearchText(e.target.value)}
            className="form-control ms-auto"
            placeholder="Search"
            style={{ maxWidth: "250px" }}
            value={searchText}
          />
        )}
      </div>
    );
  }, [stableFiltersConfig, searchable, activeFilters, handleFilterChange, searchText]);

  return (
    <div className="data-table-container">
      {filterControls}
      
      <div className="table-responsive">
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey={(record) => record._id}
          pagination={{ pageSize: defaultPageSize }}
          rowSelection={selectable ? {
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          } : null}
          expandable={expandable || defaultExpandable}
        />
      </div>
    </div>
  );
};

// FilterDropdown remains the same
const FilterDropdown = ({
  title,
  options = [],
  activeValue,
  onChange,
  customRender
}) => {
  if (customRender) {
    return customRender({ activeValue, onChange });
  }

  return (
    <div className="dropdown me-2">
      <button
        className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center"
        data-bs-toggle="dropdown"
      >
        {title}{activeValue ? `: ${activeValue}` : ''}
      </button>
      <ul className="dropdown-menu dropdown-menu-end p-3">
        {options?.map((option) => (
          <li key={option}>
            <button
              className={`dropdown-item rounded-1 ${activeValue === option ? 'active' : ''}`}
              onClick={() => onChange(option)}
            >
              {option}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default DataTable;